const decodeImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image()
    image.onload = () => resolve(image)
    image.onerror = () => reject(new Error('Image could not be decoded'))
    image.src = src
  })

const toDataUrl = (image: HTMLImageElement): string => {
  const canvas = document.createElement('canvas')
  canvas.width = image.width
  canvas.height = image.height

  const context = canvas.getContext('2d')
  if (!context) throw new Error('Canvas 2d context is unavailable')

  context.drawImage(image, 0, 0)
  return canvas.toDataURL()
}

/**
 * pdfmake needs image bytes inline, and the log photo URLs are presigned S3
 * links the page has already rendered in plain `<img>` tags. Those loads send no
 * Origin, so S3 skips CORS evaluation and the cached response has no
 * Access-Control-Allow-Origin — re-reading the same URL through a crossOrigin
 * image is then blocked by that cached response rather than by S3. A
 * cache-busting param is not an option because SigV4 signs the query string, so
 * fetch the bytes past the cache instead and decode from a blob URL, which
 * leaves the canvas untainted.
 */
export const loadImageAsDataUrl = async (url: string): Promise<string> => {
  const response = await fetch(url, { cache: 'reload' })
  if (!response.ok) {
    throw new Error(`Image request failed with status ${response.status}`)
  }

  const objectUrl = URL.createObjectURL(await response.blob())
  try {
    return toDataUrl(await decodeImage(objectUrl))
  } finally {
    URL.revokeObjectURL(objectUrl)
  }
}
